import { MGMAuth } from '@mgmresorts/mgm-auth-js-sdk';

/**
 * Utility functions for passkey/WebAuthn support detection
 */

/**
 * Checks if the current browser and device support WebAuthn/Passkeys
 * @returns Promise<boolean> - true if passkeys are supported, false otherwise
 */
export const isPasskeySupported = async (authClient: MGMAuth, mgmId?: string): Promise<boolean> => {
  try {
    const mfaManager = await authClient.mfa(mgmId);

    const support = await mfaManager?.checkPasskeySupport?.();
    if (support) {
      window.mgm?.logger?.info('passkey.ts: isPasskeySupported: support', {
        support: JSON.stringify(support),
      });
      return support.isSupported;
    } else {
      return false;
    }
  } catch (error) {
    window.mgm?.logger?.warn('passkey.ts: isPasskeySupported: error', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Combined check for both device support and user enrollment
 * @param email - User's email address
 * @returns Promise<boolean> - true if both device supports passkeys AND user has them enrolled
 */
export const canUsePasskey = async (authClient: MGMAuth, mgmId?: string): Promise<boolean> => {
  try {
    const [deviceSupported] = await Promise.all([isPasskeySupported(authClient, mgmId)]);
    window.mgm?.logger?.info('passkey.ts: canUsePasskey: deviceSupported', {
      deviceSupported: String(deviceSupported),
    });

    return deviceSupported;
  } catch (error) {
    window.mgm?.logger?.warn('passkey.ts: canUsePasskey: error', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Browser and platform specific passkey support detection
 * @returns object with detailed support information
 */
export const getPasskeySupportDetails = async () => {
  const details = {
    webAuthnSupported: Boolean(window.PublicKeyCredential),
    conditionalUISupported: false,
    userVerifyingPlatformAuthenticatorAvailable: false,
    browser: getBrowserInfo(),
    platform: getPlatformInfo(),
  };

  try {
    if (details.webAuthnSupported) {
      details.conditionalUISupported = (await PublicKeyCredential.isConditionalMediationAvailable?.()) || false;
      details.userVerifyingPlatformAuthenticatorAvailable =
        (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()) || false;
    }
  } catch (error) {
    window.mgm?.logger?.warn('passkey.ts: getPasskeySupportDetails: error', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  return details;
};

/**
 * Get browser information
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';

  return 'Unknown';
};

/**
 * Get platform information
 */
const getPlatformInfo = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';

  return 'Unknown';
};
